import { apiClient, isRemoteApiEnabled } from '@/api/client'

/**
 * Store settings — everything the owner can change without a redeploy: the
 * delivery numbers, and the branding and contact details.
 *
 * `GET /settings` is public: none of it is secret. The delivery numbers are
 * shown to every shopper anyway, and the brand name and contact details are
 * printed in the header and footer of every page.
 *
 * The delivery numbers here are display values only. The backend recomputes the
 * fee from its own copy when the order is created, and that is the figure the
 * courier collects — so a stale value here is a cosmetic bug, never a pricing
 * one.
 */

/**
 * @typedef {object} StoreSettings
 * @property {number} deliveryFee               Inside Dhaka city.
 * @property {number} deliveryFeeOutsideDhaka   Everywhere else.
 * @property {number} freeDeliveryThreshold
 * @property {string} brandName
 * @property {string} brandTagline
 * @property {string} supportPhone   Digits only, no '+' — rendered as `+{value}`.
 * @property {string} whatsappNumber Same format — the chat widget's wa.me target.
 * @property {string} messengerPageId  Page id or handle, i.e. the part after m.me/.
 * @property {string} contactEmail
 * @property {string} promoVideoUrl  Homepage video (YouTube/Vimeo/.mp4); empty hides the section.
 */

/** @returns {Promise<StoreSettings>} */
export async function getSettings() {
  return apiClient.get('/settings')
}

/**
 * Admin-only. Send only the fields being changed; omitted ones are left alone.
 *
 * @param {Partial<StoreSettings>} dto
 */
export async function updateSettings(dto) {
  if (!isRemoteApiEnabled()) {
    throw new Error('Settings are unavailable: VITE_API_BASE_URL is not set.')
  }
  return apiClient.patch('/settings', dto)
}
